'use client';

import Link from 'next/link';
import { type JSX } from 'react';

import { cn, getAccessibleFocusStyles, Text } from '@videoblocks/design-system';

import { getKeywordSearchUrlForContentClass, type KeywordSearchContentClass } from '../../_utils';

interface GoToKeywordSearchLinkProps {
    /** Media type the user is currently viewing; picks the keyword-search page. */
    contentClass: KeywordSearchContentClass;
    className?: string;
}

/** Up-right arrow glyph for the keyword-search link. Inherits color via `currentColor`. */
function ArrowUpRightIcon(): JSX.Element {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
            className="h-3.5 w-3.5 shrink-0"
        >
            <path
                fill="currentColor"
                d="M12.402 14.063a.94.94 0 0 1-.88-1l.48-7.502a.167.167 0 0 0-.285-.129l-8.134 8.135a.942.942 0 0 1-1.332-1.331l8.134-8.134a.167.167 0 0 0-.128-.285l-7.503.477a.942.942 0 1 1-.118-1.879l9.72-.618a1.568 1.568 0 0 1 1.665 1.665l-.619 9.72a.94.94 0 0 1-1 .88"
            />
        </svg>
    );
}

/**
 * Results-pane header link back to the classic keyword-search experience,
 * as an empty search for the media type currently displayed.
 */
export function GoToKeywordSearchLink({ contentClass, className }: GoToKeywordSearchLinkProps): JSX.Element {
    return (
        <Link
            href={getKeywordSearchUrlForContentClass(contentClass)}
            prefetch={false}
            className={cn(
                'inline-flex items-center gap-1 whitespace-nowrap rounded-sm text-blue-700 hover:underline',
                getAccessibleFocusStyles(),
                className,
            )}
        >
            <Text asChild fontSize="sm" fontWeight="semibold">
                <span>Go to keyword search</span>
            </Text>
            <ArrowUpRightIcon />
        </Link>
    );
}
